import { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useFormik } from "formik";

import { useParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { Button, Grid2, Stack } from "@mui/material";

import ContainerLoading from "../components/ContainerLoading";
import { Input } from "../components/Input";
import PageHeader from "../components/PageHeader";

import ProductsDelete from "../components/ProductsDelete";
import DropComponent from "../components/Dropzone";

import {
  listStoreProductById,
  updateStoreProducts,
} from "../services/products";

import {
  formatNumber,
  formatPrice,
  maskMoney,
  unMaskMoney,
} from "../helpers/format";

const ProductsDetails = () => {
  const { id } = useParams();
  const [imgs, setImgs] = useState<(File | { id: number; img: string })[]>([]);
  const [deleteAlert, setDeleteAlert] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["listStoreProductById" + id],
    queryFn: async () => await listStoreProductById(Number(id)),
    initialData: {},
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (body: any) => await updateStoreProducts(body),
    onSuccess: () => {
      refetch();
      toast.success("Produto atualizado");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const {
    values,
    touched,
    errors,
    handleSubmit,
    setFieldValue,
    handleBlur,
    handleChange,
  } = useFormik({
    initialValues: {
      name: "",
      description: "",
      price: 0,
      cost: 0,
      qty: 0,
      qtyMinimal: 0,
      sku: "",
    },
    onSubmit: (values) => {
      if (isEmpty(imgs)) {
        toast.info("Selecione uma imagem do produto.");

        return;
      }
      const oldImgs = imgs?.filter((i: any) => i?.id);
      const images = imgs?.filter((i: any) => !i?.id);

      mutate({
        ...values,
        price: unMaskMoney(values?.price),
        imgs: images,
        oldImgs: JSON.stringify(oldImgs || []),
      });
    },
  });

  useEffect(() => {
    if (data) {
      setFieldValue("id", data?.id);
      setFieldValue("name", data?.name);
      setFieldValue("description", data?.description);
      setFieldValue("price", data?.price);
      setFieldValue("cost", data?.cost || 0);
      setFieldValue("qty", data?.qty || 0);
      setFieldValue("sku", data?.sku);
      setFieldValue("qtyMinimal", data?.qtyMinimal || 0);
    }
  }, [data]);

  useEffect(() => {
    if (!isEmpty(data?.images)) {
      setImgs(data?.images);
    }
  }, [data]);

  const loading = isLoading || isFetching || isPending;
  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Detalhes do produto"
          subtitle="Informações gerais do produto."
          back
        />

        <Grid2 size={12}>
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="name"
                  name="name"
                  label="Nome"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && !!errors.name}
                  helperText={Boolean(touched.name) && Boolean(errors.name)}
                />
              </Grid2>{" "}
              <Grid2 size={6}>
                <Input
                  id="sku"
                  name="sku"
                  label="SKU"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.sku}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.sku && !!errors.sku}
                  helperText={Boolean(touched.sku) && Boolean(errors.sku)}
                />
              </Grid2>
              <Grid2 size={6}>
                <Input
                  id="price"
                  name="price"
                  label="Preço"
                  type="text"
                  fullWidth
                  size="small"
                  value={formatPrice(values.price)}
                  onChange={(e) =>
                    setFieldValue("price", maskMoney(e.target.value))
                  }
                  onBlur={handleBlur}
                  error={touched.price && !!errors.price}
                  helperText={Boolean(touched.price) && Boolean(errors.price)}
                />
              </Grid2>{" "}
              <Grid2 size={6}>
                <Input
                  id="cost"
                  name="cost"
                  label="Custo ( opcional )"
                  type="text"
                  fullWidth
                  size="small"
                  value={formatPrice(values.cost)}
                  onChange={(e) =>
                    setFieldValue("cost", maskMoney(e.target.value))
                  }
                  onBlur={handleBlur}
                  error={touched.cost && !!errors.cost}
                  helperText={Boolean(touched.cost) && Boolean(errors.cost)}
                />
              </Grid2>
              <Grid2 size={6}>
                <Input
                  id="qty"
                  name="qty"
                  label="Estoque"
                  type="text"
                  fullWidth
                  size="small"
                  value={formatNumber(values.qty)}
                  onChange={(e) => setFieldValue("qty", e.target.value)}
                  onBlur={handleBlur}
                  error={touched.qty && !!errors.qty}
                  helperText={Boolean(touched.qty) && Boolean(errors.qty)}
                />
              </Grid2>{" "}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="qtyMinimal"
                  name="cost"
                  label="Estoque mínimo ( opcional )"
                  type="text"
                  fullWidth
                  size="small"
                  value={formatNumber(values.qtyMinimal)}
                  onChange={(e) => setFieldValue("qtyMinimal", e.target.value)}
                  onBlur={handleBlur}
                  error={touched.qtyMinimal && !!errors.qtyMinimal}
                  helperText={
                    Boolean(touched.qtyMinimal) && Boolean(errors.qtyMinimal)
                  }
                />
              </Grid2>
              <Grid2 size={12}>
                <Input
                  id="description"
                  name="description"
                  label="Descrição"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && !!errors.description}
                  helperText={
                    Boolean(touched.description) && Boolean(errors.description)
                  }
                />
              </Grid2>
              <Grid2 size={12}>
                <DropComponent
                  onDrop={(acceptedFiles: File[]) => {
                    const allFiles = [...imgs, ...acceptedFiles].slice(0, 4);
                    setImgs(allFiles);
                  }}
                  files={imgs}
                  setFiles={setImgs}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "flex-end",
              width: "100%",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={() => setDeleteAlert(true)}
            >
              Excluir
            </Button>
            <Button variant="contained" onClick={() => handleSubmit()}>
              Atualizar
            </Button>
          </Stack>
        </Grid2>
      </Grid2>
      <ProductsDelete
        open={deleteAlert}
        onClose={() => setDeleteAlert(false)}
        id={String(id)}
        onAction={refetch}
      />
    </ContainerLoading>
  );
};

export default ProductsDetails;
